import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { TabPanel, TabView } from "primereact/tabview";
import type { TabViewTabChangeEvent } from "primereact/tabview";

import { C } from "sj-ui-toolkit/src/atlas/utils";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getDirectoryClient } from "sj-ui-toolkit/src/client";
import {
  getCurrentSelectedTenant,
  getIsImpersonating,
  impersonateAsync,
} from "sj-ui-toolkit/src/utils/user";

import { AppContext } from "@/context/AppContext";
import { loadUserByRailsId } from "@/api/users";
import { loadUserProfileStats } from "@/api/progressReport";
import type { UserProfileStatsData } from "@/api/progressReport";
import { UserProfileHeader } from "@/components/UserProfileHeader";
import type { UserData } from "@/pages/Users";

// ---------------------------------------------------------------------------
// Inline tab bar (reads route, drives navigation)
// ---------------------------------------------------------------------------

interface UserTabsProps {
  userId: string | null;
}

function UserTabs({ userId }: UserTabsProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { label: "Profile", icon: "pi pi-user", path: "profile" },
    { label: "History", icon: "pi pi-history", path: "history" },
    { label: "Certificates", icon: "pi pi-id-card", path: "certificates" },
  ];

  const activeIndex = Math.max(
    tabs.findIndex(t => location.pathname.endsWith(t.path)),
    0
  );

  const handleTabChange = (e: TabViewTabChangeEvent) => {
    const tab = tabs[e.index];
    if (!tab) return;
    const query = userId ? `?userId=${userId}` : "";
    void navigate(`${tab.path}${query}`);
  };

  return (
    <div className="flex justify-start my-6 [&_.p-tabview-nav-link]:bg-transparent">
      <TabView
        aria-label="User details"
        activeIndex={activeIndex}
        onTabChange={handleTabChange}
        panelContainerClassName="hidden"
      >
        {tabs.map((tab, index) => (
          <TabPanel
            key={tab.label}
            header={
              <div
                className={C(
                  "flex items-center gap-2 pb-2 border-b",
                  activeIndex === index
                    ? "font-semibold text-color border-[var(--text-color)]"
                    : "text-secondary border-transparent hover:text-color"
                )}
              >
                <i className={tab.icon} aria-hidden="true" />
                {tab.label.toUpperCase()}
              </div>
            }
          />
        ))}
      </TabView>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserDetail page
// ---------------------------------------------------------------------------

export default function UserDetail() {
  const { user_id: id } = useParams<{ user_id: string }>();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId"); // Rails sequential ID

  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { isSjAdmin } = useUserContext();
  const {
    state: { viewOnly: _viewOnly },
  } = useContext(AppContext);
  const impersonating = getIsImpersonating();
  const { showToast } = useToast();

  const [user, setUser] = useState<UserData | null>(null);
  const [profileData, setProfileData] = useState<UserProfileStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  useBreadcrumb(
    user?.email
      ? {
          label: user.email,
          route: `${AdminAppRoutes.routes.users.routes.id.route.replace(":user_id", id ?? "")}`,
          display: "page",
        }
      : null
  );

  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    setIsProfileLoading(true);
    setUser(null);
    setProfileData(null);

    const loadUser = async () => {
      const svcDirectoryClient = getDirectoryClient();
      try {
        if (userId) {
          const [directoryRes, railsData] = await Promise.all([
            svcDirectoryClient.getUser(currentTenantName, id),
            loadUserByRailsId(userId),
          ]);
          setUser({
            ...railsData.user,
            ...directoryRes.data,
            lessons: railsData.lessons,
            passedBelts: railsData.passedBelts,
          } as UserData);
        } else {
          const directoryRes = await svcDirectoryClient.getUser(currentTenantName, id);
          setUser((directoryRes.data ?? null) as UserData | null);
        }
      } catch (e) {
        console.error("Error loading user:", e);
        showToast({ severity: "error", detail: "Failed to load user" });
      } finally {
        setIsLoading(false);
      }

      if (userId) {
        try {
          const stats = await loadUserProfileStats(userId);
          setProfileData(stats);
        } catch (e) {
          console.error("Error loading profile stats:", e);
        } finally {
          setIsProfileLoading(false);
        }
      } else {
        setIsProfileLoading(false);
      }
    };

    void loadUser();
  }, [id, userId, currentTenantName, showToast]);

  const handleImpersonateClick = () => {
    if (user?.email) void impersonateAsync(user.email);
  };

  const { totalTime, lastMonthTotalTime, lastMonthPoints, totalPoints, currentLevel } =
    profileData ?? {};

  const stats = {
    totalPoints: totalPoints ?? null,
    lastMonthPoints: lastMonthPoints ?? null,
    totalTime: totalTime ?? null,
    lastMonthTime: lastMonthTotalTime ?? null,
  };

  const level = currentLevel?.levelName ?? null;

  return (
    <>
      <UserProfileHeader
        user={user}
        isSjAdmin={isSjAdmin}
        impersonating={impersonating}
        trialMode={false}
        handleImpersonateClick={handleImpersonateClick}
        isLoading={isProfileLoading}
        stats={stats}
        level={level}
      />
      <UserTabs userId={userId} />
      <Outlet context={{ user, isLoading }} />
    </>
  );
}
